export const currentDateAsString = (): string => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0'); // Ensure two digits
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
};

export const extractAndConvertFrom = (dateTime: string) => {
    if (!dateTime) {
        return '';
    }
    return dateTime.split(' ')[0].split('-').reverse().join('/');
};

export const isDatePast = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const inputDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate < today;
};

export const formatDateToBR = (date: string | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
};

/** dd/mm/aaaa a partir de date/ISO. Baseado em string (sem `new Date`), então NÃO
 *  aplica fuso — use para campos que são data-calendário (vencimento, emissão) e evite D-1. */
export const formatDateBR = (value: string | null | undefined): string => {
    if (!value) return '-';
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
};

/**
 * O fuso em que o backoffice LÊ o dia.
 *
 * A operação é brasileira e o fechamento do dia é o de Brasília, então a data de um lançamento é a
 * mesma para todo mundo que olha a tela — independentemente de onde o navegador está. Usar o fuso
 * LOCAL faria um PIX das 22h em São Paulo cair num dia para quem está no Acre e noutro para quem
 * está em Campo Grande, e os dois estariam lendo o mesmo banco.
 *
 * Já era a prática do sistema, repetida à mão em cada lugar que formatava data. Fica aqui para as
 * telas novas herdarem em vez de escolherem.
 */
export const BRASILIA_TIMEZONE = 'America/Sao_Paulo';

/** dd/mm/aaaa HH:MM no fuso de São Paulo, a partir de um INSTANTE em UTC (ISO com offset).
 *  Use para timestamps (payment_date, movement_date) — a conversão de fuso é feita aqui. */
export const formatDateTimeBR = (value: string | null | undefined): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: BRASILIA_TIMEZONE,
    });
};

/**
 * O DIA (aaaa-mm-dd) de um INSTANTE, no calendário de Brasília.
 *
 * ⚠️ Para timestamps (payment_date, movement_date), que o banco guarda em UTC. Cortar a string em
 * `slice(0, 10)` daria o dia UTC: um PIX das 22h30 do dia 1º em São Paulo é 01h30 do dia 2 em UTC,
 * e a planilha diria que ele entrou no dia 2. O mesmo motivo do `formatDateTimeBR` logo acima.
 *
 * `en-CA` porque é o locale que formata como aaaa-mm-dd — o formato que as demais funções daqui
 * esperam.
 */
export const toBrasiliaDateISO = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: BRASILIA_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
};

/** Hoje em aaaa-mm-dd no calendário de BRASÍLIA, qualquer que seja o fuso do navegador.
 *  Para telas que conversam com a API em `tz=America/Sao_Paulo` e para vencimento (a data de
 *  negócio é a de Brasília). `toISOString().split('T')[0]` daria o dia UTC: depois das 21h a tela
 *  abria no dia seguinte e marcava vencido um dia antes (review da PR #1208). */
export const todayInBrasiliaISO = (): string => toBrasiliaDateISO(new Date().toISOString()) as string;

/** Hoje em aaaa-mm-dd, no fuso de quem está olhando a tela.
 *  Montado a partir dos componentes LOCAIS de propósito: `toISOString()` converte para UTC e
 *  devolveria ontem em qualquer horário depois das 21h no Brasil — justo quando se olha vencido. */
export const todayISO = (): string => {
    const d = new Date();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
};

/** Data-calendário a `days` de hoje (negativo = passado), em aaaa-mm-dd.
 *  `new Date(ano, mês, dia + n)` normaliza virada de mês, ano e bissexto sozinho. */
export const shiftDaysISO = (days: number): string => {
    const hoje = new Date();
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + days);
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
};

export const formatDateFromBRToISO = (dateString: string): string => {
    if (!dateString)
        return '';
    const [day, month, year] = dateString.split('/');
    if (!day || !month || !year)
        return '';
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Parseia uma string de data para Date local, tratando YYYY-MM-DD como data local
 * (não UTC) para evitar deslocamento de timezone.
 *
 * AF-3447: new Date("YYYY-MM-DD") é interpretado como UTC pela spec JS, o que exibe o dia
 * anterior em BRT. Timestamps ISO completos (com hora e offset) são parseados normalmente,
 * porque aí o instante é inequívoco e a conversão para o fuso local é a correta.
 *
 * AF-3581: promovido de cópias locais em MonitoringPage e ExportDropdown para cá, porque
 * componentes novos precisavam do mesmo tratamento e o formatDateToBR acima não o faz —
 * ele usa new Date() direto e sofre o off-by-one com data sem hora.
 */
export const parseLocalDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const parsed = new Date(year, month - 1, day);

        // O construtor do Date normaliza valores fora do intervalo em vez de falhar:
        // new Date(2026, 1, 30) devolve 02/03/2026. O risco não é a data absurda, é a
        // plausível — 2026-02-30 apareceria na tela como 02/03 sem nenhum sinal de erro.
        // O ramo de ISO completo abaixo já é estrito (devolve Invalid Date), então sem
        // esta conferência os dois ramos teriam rigor diferente.
        const fieldsPreserved =
            parsed.getFullYear() === year &&
            parsed.getMonth() === month - 1 &&
            parsed.getDate() === day;

        return fieldsPreserved ? parsed : null;
    }

    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Data no formato brasileiro, sem deslocamento de timezone para data sem hora.
 * Prefira esta função a formatDateToBR quando a origem puder ser YYYY-MM-DD.
 */
export const formatLocalDateToBR = (dateStr: string | null | undefined): string => {
    const parsed = parseLocalDate(dateStr);
    return parsed ? parsed.toLocaleDateString('pt-BR') : '';
};
